import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React from 'react'

import Button from '@/app/component/atom/button'
import ExternalLink from '@/app/component/atom/external-link'
import { Input } from '@/app/component/atom/form'
import { OpenEndedFormat } from '@/app/module/campaigns/types'
import { validateOpenEndedFormat } from '@/app/module/campaigns/utils/open-ended-validation'

type Props = {
  examplesPosition?: 'above' | 'below'
  inputRef?: React.RefObject<HTMLInputElement>
  required?: boolean
  showErrorsOnMount?: boolean
  value: string
  onChange: (value: string) => void
}

const examples = [
  { label: 'must not contain spaces', value: '^\\S+$' },
  { label: 'yes only', value: '^yes$' },
  { label: 'one letter followed by 8 digits', value: '^[A-Za-z][0-9]{8}$' },
  { label: 'a valid email address', value: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$' },
]

const ResponseRegexInput: React.FC<Props> = ({
  examplesPosition = 'above',
  inputRef,
  required = false,
  showErrorsOnMount = false,
  value,
  onChange,
}) => {
  const [errorVisible, setErrorVisible] = React.useState(showErrorsOnMount)
  let error = ''
  if (errorVisible && (required || value)) {
    error = validateOpenEndedFormat({ format: OpenEndedFormat.Regexp, formatOption: value })
  }
  const handleChange = (nextValue: string) => {
    setErrorVisible(false)
    onChange(nextValue)
  }
  const exampleLinks = !value && (
    <Typography color="textSecondary" component="div" variant="caption">
      Examples:{' '}
      {examples.map((example, index) => (
        <React.Fragment key={example.value}>
          {index > 0 && ', '}
          <Button
            size="small"
            sx={{ minWidth: 0, p: 0, textTransform: 'none', verticalAlign: 'baseline' }}
            onClick={() => handleChange(example.value)}
          >
            {example.label}
          </Button>
        </React.Fragment>
      ))}
    </Typography>
  )

  return (
    <Box>
      {examplesPosition === 'above' && exampleLinks}
      <Input
        editable
        error={error}
        inputRef={inputRef}
        label="Regular expression"
        name="open-ended-regex"
        placeholder="Enter a regular expression"
        required={required}
        value={value}
        onBlur={() => setErrorVisible(true)}
        onChange={({ value: nextValue }) => handleChange(nextValue)}
      />
      <Typography color="textSecondary" component="p" sx={{ mt: 0.5 }} variant="caption">
        Use an RE2-compatible regular expression. Some advanced regular expression features may not be supported.{' '}
        <ExternalLink href="https://github.com/google/re2/wiki/syntax">See RE2 syntax.</ExternalLink>
      </Typography>
      {examplesPosition === 'below' && exampleLinks}
    </Box>
  )
}

export default ResponseRegexInput
